import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useStaffStore } from '../../../../stores/appStaffStore';

/**
 * Approve / reject / set-pending for one staff member. Shared so the card's quick
 * actions and the detail panel's status control stay in lockstep — the store already
 * updates the member in place, so neither caller needs to reload the list.
 */
export function useStaffStatusActions(staffId: string) {
  const { t } = useTranslation();
  const approveStaff = useStaffStore((s) => s.approveStaff);
  const rejectStaff = useStaffStore((s) => s.rejectStaff);
  const setPendingStaff = useStaffStore((s) => s.setPendingStaff);

  const approve = useCallback(async () => {
    try {
      await approveStaff(staffId);
    } catch (err) {
      console.error(err);
      alert(t('staff.approveError'));
    }
  }, [approveStaff, staffId, t]);

  const reject = useCallback(async () => {
    try {
      await rejectStaff(staffId);
    } catch (err) {
      console.error(err);
      alert(t('staff.rejectError'));
    }
  }, [rejectStaff, staffId, t]);

  const setPending = useCallback(async () => {
    try {
      await setPendingStaff(staffId);
    } catch (err) {
      console.error(err);
      alert(t('staff.updateStatusError'));
    }
  }, [setPendingStaff, staffId, t]);

  return { approve, reject, setPending };
}
